import { captureException } from "@sentry/browser";
import { redirect } from "react-router-dom";

import type { GetCurrentUserQuery } from "generated/graphql";
import { AccessEnum } from "generated/graphql";
import { FeatureSlugsEnum } from "globalConstants/features";
import {
  ACTORS_PATH,
  AGENTS_EXECUTIONS,
  DASHBOARD_PATH,
  DEFAULT_PAGE_PATH,
  GLOBAL_TRACKING_PATH,
  INSIGHTS_PATH,
  INTEGRATIONS_DOCS_PATH,
  INTEGRATIONS_LOGS_PATH,
  MERCANTE_PATH,
  NOT_AUTHORIZED_PATH,
  OPERATIONS_PATH,
  OPERATION_PATHS,
  PORT_TERMINALS_PATH,
  REQUEST_PATH,
  ROOT_AGENTS,
  SETTINGS_PATHS,
} from "routes/paths";
import { generateApolloClientInstance, isAuthTokenRejectedError } from "settings/apollo";
import { getIdToken, logout } from "settings/auth0";
import { hasFeatureAccess } from "utils/hasFeatureAccess";

import { useCurrentUserCacheStore } from "stores/useCurrentUserCacheStore";

import GET_CURRENT_USER from "./graphql/getCurrentUser";
import type { TLoaderCurrentUserFn } from "./types";

const ROUTES_ACCESS_CONTROL_BY_FEATURE_SLUG = {
  [FeatureSlugsEnum.AGENTS]: [
    { path: ROOT_AGENTS, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
    { path: AGENTS_EXECUTIONS, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
    { path: `${AGENTS_EXECUTIONS}/:execution_id`, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
  ],
  [FeatureSlugsEnum.USERS]: [{ path: SETTINGS_PATHS.users, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
  [FeatureSlugsEnum.LEAD_TIMES]: [
    { path: OPERATION_PATHS.lead_times, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
    { path: OPERATION_PATHS.configure_lead_times, allowedTypes: [AccessEnum.Full] },
  ],
  [FeatureSlugsEnum.DASHBOARD]: [{ path: DASHBOARD_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
  [FeatureSlugsEnum.ACTORS]: [{ path: ACTORS_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
  [FeatureSlugsEnum.API_DOCS]: [{ path: INTEGRATIONS_DOCS_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
  [FeatureSlugsEnum.INTEGRATION_LOGS]: [
    { path: INTEGRATIONS_LOGS_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
    { path: `${INTEGRATIONS_LOGS_PATH}/:operation_id`, allowedTypes: [AccessEnum.ReadOnly] },
    {
      path: `${INTEGRATIONS_LOGS_PATH}/:operation_id/${REQUEST_PATH}/:request_id`,
      allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly],
    },
  ],
  [FeatureSlugsEnum.MERCANTE]: [{ path: MERCANTE_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
  [FeatureSlugsEnum.PORT_TERMINALS]: [
    { path: PORT_TERMINALS_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
  ],
  [FeatureSlugsEnum.GLOBAL_TRACKING]: [
    { path: GLOBAL_TRACKING_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
  ],
  [FeatureSlugsEnum.CREDENTIALS]: [
    { path: SETTINGS_PATHS.credentials, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
  ],
  [FeatureSlugsEnum.WORKSPACES]: [
    { path: SETTINGS_PATHS.workspace, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] },
  ],
  [FeatureSlugsEnum.INSIGHTS]: [{ path: INSIGHTS_PATH, allowedTypes: [AccessEnum.Full, AccessEnum.ReadOnly] }],
};

export const loaderCurrentUser: TLoaderCurrentUserFn = async args => {
  const apolloClient = generateApolloClientInstance(getIdToken, logout);

  let user: GetCurrentUserQuery["currentUser"] | null = null;

  const userCache = useCurrentUserCacheStore.getState();

  if (!userCache.isStale() && userCache.user !== null) {
    user = userCache.user;
  } else {
    try {
      const {
        data: { currentUser },
      } = await apolloClient.query<GetCurrentUserQuery>({ query: GET_CURRENT_USER });

      user = currentUser;
      userCache.setUser(currentUser);
    } catch (error) {
      captureException(error);

      if (isAuthTokenRejectedError(error)) {
        userCache.invalidate();

        await logout();
      }

      return { currentUser: null };
    }
  }

  const { url } = args.request;

  const { pathname } = new URL(url);

  const pathnameWithoutStartTrailingSlash = pathname.slice(1);

  if (user && pathnameWithoutStartTrailingSlash === "") {
    throw redirect(DEFAULT_PAGE_PATH);
  }

  Object.entries(ROUTES_ACCESS_CONTROL_BY_FEATURE_SLUG).forEach(([featureSlug, route]): void => {
    const currentRoute = route.find(path => path.path === pathnameWithoutStartTrailingSlash);

    if (currentRoute) {
      const userHasAccess = hasFeatureAccess({
        featureSlug: featureSlug as FeatureSlugsEnum,
        allowedAccessTypes: currentRoute.allowedTypes,
        userFeatureAccesses: user?.featureAccesses ?? [],
      });

      if (!userHasAccess) {
        if (pathnameWithoutStartTrailingSlash === DASHBOARD_PATH) {
          throw redirect(OPERATIONS_PATH);
        }

        throw redirect(NOT_AUTHORIZED_PATH);
      }
    }
  });

  return { currentUser: user };
};
